import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FieldContainer,
  Label,
  Required,
  InputWrapper,
  ColorInput,
  ColorSwatch,
  ColorName,
  ChevronIcon,
  Dropdown,
  DropdownOption,
  OptionName,
  CheckIcon,
  HelperText,
} from './StyledComponents';

const ChevronSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * ColorSelect - A dropdown select for choosing colors with visual swatches
 *
 * @param {string} label - Label text for the select
 * @param {boolean} required - Whether the field is required (shows red asterisk)
 * @param {string} value - The selected color value (hex code)
 * @param {Function} onChange - Callback when value changes (receives color value string)
 * @param {string} placeholder - Placeholder text when no color selected
 * @param {Array} options - Array of {value, label} objects (value is hex code, label is color name)
 * @param {string} helperText - Helper/error text below the select
 * @param {boolean} error - Whether the select has an error state
 * @param {string} width - Width of the select (default: "100%")
 * @param {string} maxWidth - Max width of the select
 * @param {boolean} disabled - Whether the select is disabled
 * @param {boolean} allowEmpty - Whether to show a "No Color" option (default: true)
 * @param {string} emptyLabel - Label for the empty option (default: "No Color")
 */
const ColorSelect = ({
  label,
  required = false,
  value,
  onChange,
  placeholder = 'Select a color...',
  options = [],
  helperText,
  error = false,
  width = '100%',
  maxWidth,
  disabled = false,
  allowEmpty = true,
  emptyLabel = 'No Color',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Build options list with optional empty option
  const allOptions = allowEmpty
    ? [{ value: '', label: emptyLabel }, ...options]
    : options;

  // Find the selected option
  const selectedOption = allOptions.find((opt) => opt.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    setHighlightedIndex(-1);
  };

  const handleSelect = useCallback(
    (option) => {
      onChange(option.value);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < allOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightedIndex >= 0 && allOptions[highlightedIndex]) {
          handleSelect(allOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleDropdownMouseDown = (e) => {
    // Prevent blur from firing when clicking dropdown
    e.preventDefault();
  };

  return (
    <FieldContainer width={width} maxWidth={maxWidth} ref={containerRef}>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}

      <InputWrapper>
        <ColorInput
          ref={inputRef}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          isOpen={isOpen}
          error={error}
          disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <ColorSwatch color={selectedOption?.value || null} />
          <ColorName hasValue={!!selectedOption?.value}>
            {selectedOption?.label || placeholder}
          </ColorName>
          <ChevronIcon isOpen={isOpen}>
            <ChevronSvg />
          </ChevronIcon>
        </ColorInput>

        {isOpen && (
          <Dropdown role="listbox" onMouseDown={handleDropdownMouseDown}>
            {allOptions.map((option, index) => {
              const isSelected = option.value === value;
              const isHighlighted = highlightedIndex === index;

              return (
                <DropdownOption
                  key={option.value || 'empty'}
                  isSelected={isSelected}
                  isHighlighted={isHighlighted}
                  onClick={() => handleSelect(option)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <ColorSwatch color={option.value || null} />
                  <OptionName>{option.label}</OptionName>
                  {isSelected && (
                    <CheckIcon>
                      <CheckSvg />
                    </CheckIcon>
                  )}
                </DropdownOption>
              );
            })}
          </Dropdown>
        )}
      </InputWrapper>

      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </FieldContainer>
  );
};

ColorSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  helperText: PropTypes.string,
  error: PropTypes.bool,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  disabled: PropTypes.bool,
  allowEmpty: PropTypes.bool,
  emptyLabel: PropTypes.string,
};

export default ColorSelect;
